
import * as THREE from "../../../libs/three.js/build/three.module.js";
import {
	selectFrontier,
	ensureHierarchyLoaded,
	ensurePointsLoaded,
	collectNodePoints,
	assertTranslationOnlyTransform,
} from "../volume/RoiPointLoader.js";

// ─────────────────────────────────────────────────────────────────────────
// Adapter between the pure pipe tracker's `queryPoints(center, tangent,
// radialExtent, axialHalfLength) -> Promise<Vector3[]>` interface and the
// live Potree octree(s).
//
// Deliberately reuses volume/RoiPointLoader.js's primitives (selectFrontier,
// ensureHierarchyLoaded, collectNodePoints, assertTranslationOnlyTransform) so
// there is ONE octree-ROI code path in the project, not two.
//
// Camera / LOD independence (spec §4, §30-C): this only ever traverses
// `pointcloud.pcoGeometry.root` (the geometry octree) and fetches the nodes
// it needs. It never reads `visibleNodes`, the active camera, the render
// frustum, or `Potree.pointBudget`. selectFrontier's depth decision is a pure
// function of `node.numPoints` metadata (hierarchy only - not of which nodes'
// POINTS happen to be loaded). Same pointcloud + same (center, tangent,
// extents) therefore returns the same points regardless of where the camera
// is, what LOD the renderer last chose, or what has been cached/prefetched.
//
// On-demand loading (spec §5): each call re-runs selectFrontier for its own
// ROI box and obtains whatever frontier nodes are not resident yet, so the
// tracker can follow a pipe out of the region that was visible when the user
// started.
//
// Node data (performance fase 2): a node's points are obtained through ONE
// function (getEntry) used by both the normal query and prefetch:
//   1. already in this adapter's cache                       -> reuse
//   2. a fetch for the node is already in flight             -> join it (never a second download)
//   3. the viewer already holds the node (node.loaded)       -> read its geometry (no network)
//   4. 2.0 loader available: loader.loadDecoded(node)        -> fetch + decode WITHOUT touching viewer state
//      (no Potree.maxNodesLoading gate of 4, no 20 ms polling, no LRU/GPU residency that competes with the
//       viewer's own nodes); the decode itself is identical to node.load()'s (same worker + message)
//   5. otherwise (other loaders / test mocks)                -> the old node.load() + poll path
// All frontier nodes of a query are requested in PARALLEL, then consumed in frontier order, so the
// returned points (values AND order) are exactly the same as with serial loading.
//
// Cache (spec §6): per-node, the node's OWN decoded points are extracted
// ONCE into a compact Float32 buffer in pointcloud-LOCAL coordinates (small
// numbers - full Float32 precision, unlike UTM-scale world coords) and
// reused for every overlapping ROI. LRU by node (and optionally by point
// count), capped. Owned by this adapter instance only (one per Find rør run);
// cleared on dispose. Independent of Potree's own node lifecycle.
//
// Prefetch (performance fase 2): queryPoints.prefetch(center, tangent, radial, axial) only WARMS the
// cache for a future ROI (same frontier selection as a query, same getEntry). It returns nothing,
// never throws, never blocks tracking, and never changes what a query returns.
// ─────────────────────────────────────────────────────────────────────────

const DEFAULT_POINT_BUDGET = 200000;   // per-ROI analysis budget (NOT the render budget)
// Node-cachen (fase 2): målt på HV Vand 2 (412 forskellige noder, ~1 mio. punkter) giver 64 noder konstant gen-hentning, 256 stadig
// 421 hentninger i en varm kørsel og 512 nul udsmidninger. Antal noder alene styrer hukommelsen dårligt (nodestørrelsen varierer),
// så der er både en nodegrænse og en PUNKTGRÆNSE (~30 B/punkt inkl. indeks og farver => 3 mio. punkter ~ 90 MB værste tilfælde).
const DEFAULT_CACHE_NODE_LIMIT = 1024;
const DEFAULT_CACHE_POINT_LIMIT = 3000000;
const DEFAULT_MAX_PREFETCH_INFLIGHT = 16; // prefetch never queues more than this many node fetches at once

// Axis-aligned world bounding box that fully contains the oriented cylinder
// ROI (half-length `axialHalfLength` along `t`, radius `radialExtent`).
function orientedCylinderAabbHalfExtent(t, radialExtent, axialHalfLength){
	return new THREE.Vector3(
		axialHalfLength * Math.abs(t.x) + radialExtent * Math.sqrt(Math.max(0, 1 - t.x * t.x)),
		axialHalfLength * Math.abs(t.y) + radialExtent * Math.sqrt(Math.max(0, 1 - t.y * t.y)),
		axialHalfLength * Math.abs(t.z) + radialExtent * Math.sqrt(Math.max(0, 1 - t.z * t.z)),
	);
}

const perfNow = () => (typeof performance !== "undefined" && performance.now ? performance.now() : Date.now());

// A point/hierarchy load error, marked so callers can tell "could not read the cloud" from "no points here".
function markLoadFailure(e){
	const err = e instanceof Error ? e : new Error(String(e));
	err.pointLoadFailed = true;
	return err;
}

export function createOctreeRoiQuery(pointclouds, options = {}){
	const pointBudget = options.pointBudget || DEFAULT_POINT_BUDGET;
	const cacheNodeLimit = options.cacheNodeLimit || DEFAULT_CACHE_NODE_LIMIT;
	const cachePointLimit = options.cachePointLimit || DEFAULT_CACHE_POINT_LIMIT;
	const maxPrefetchInflight = options.maxPrefetchInflight ?? DEFAULT_MAX_PREFETCH_INFLIGHT;
	const shouldCancel = options.shouldCancel || (() => false);
	// per-node voxel grid on by default; `spatialIndex: false` forces the old
	// full linear per-node re-scan (baseline for the before/after measurement).
	const useGrid = options.spatialIndex !== false;
	// `legacyLoad: true` forces the old node.load() + polling path (baseline for before/after measurements)
	const useDecodedLoad = options.legacyLoad !== true;

	const clouds = (pointclouds || []).filter((pc) => pc && pc.visible && pc.pcoGeometry && pc.pcoGeometry.root);
	for(const pc of clouds) assertTranslationOnlyTransform(pc);

	// node -> {local: Float32Array (x,y,z interleaved, pointcloud-local), grid,
	//          vsize, gmin:[x,y,z], gdim:[nx,ny,nz]}
	const nodeCache = new Map();
	// node -> {promise, prefetched, used}: fetches in flight (shared by queries and prefetch)
	const inflight = new Map();
	let cachedPoints = 0;
	let prefetchInflight = 0;
	let disposed = false;
	const stats = {
		queries: 0, nodesConsidered: 0, nodesLoadedOnDemand: 0,
		cacheHits: 0, cacheMisses: 0, pointsReturned: 0, pointsScanned: 0, cacheBytes: 0, appearanceBytes: 0,
		// timing (ms, summeret): hele forespørgslen / tid brugt på at vente på node-data (frontier + indlæsning/dekodning) /
		// opbygning af node-indeks ved cache-miss / punkt-scanning
		queryMs: 0, loadWaitMs: 0, frontierWaitMs: 0, buildMs: 0, scanMs: 0,
		// fase 2: fælles nodehentning (forespørgsler + prefetch)
		inflightJoins: 0, cacheEvictions: 0, cachedNodes: 0, cachedPoints: 0,
		prefetchRequests: 0, prefetchStarted: 0, prefetchUsed: 0,
	};

	// Build a coarse uniform voxel grid over a node's (static) local points so a
	// query only scans points in voxels the ROI AABB actually overlaps, instead
	// of re-scanning every point in every AABB-touched node on every step. The
	// point arrays never change, so this is build-once per cached node. This is
	// the hot-path optimisation (spec: consecutive ROIs overlap ~50 %, so the
	// same points were re-tested every step of a long track).
	function buildGrid(local){
		let minX = Infinity, minY = Infinity, minZ = Infinity;
		let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
		for(let i = 0; i < local.length; i += 3){
			if(local[i] < minX) minX = local[i]; if(local[i] > maxX) maxX = local[i];
			if(local[i + 1] < minY) minY = local[i + 1]; if(local[i + 1] > maxY) maxY = local[i + 1];
			if(local[i + 2] < minZ) minZ = local[i + 2]; if(local[i + 2] > maxZ) maxZ = local[i + 2];
		}
		const nPts = local.length / 3 || 1;
		const extent = Math.max(maxX - minX, maxY - minY, maxZ - minZ, 1e-6);
		// aim for ~32 points per voxel
		const vsize = Math.max(extent / Math.max(1, Math.cbrt(nPts / 32)), extent / 40);
		const gdim = [
			Math.max(1, Math.ceil((maxX - minX) / vsize) + 1),
			Math.max(1, Math.ceil((maxY - minY) / vsize) + 1),
			Math.max(1, Math.ceil((maxZ - minZ) / vsize) + 1),
		];
		const grid = new Map();
		for(let i = 0; i < local.length; i += 3){
			const gx = Math.floor((local[i] - minX) / vsize);
			const gy = Math.floor((local[i + 1] - minY) / vsize);
			const gz = Math.floor((local[i + 2] - minZ) / vsize);
			const key = (gx * gdim[1] + gy) * gdim[2] + gz;
			let cell = grid.get(key);
			if(!cell){ cell = []; grid.set(key, cell); }
			cell.push(i);
		}
		return {grid, vsize, gmin: [minX, minY, minZ], gdim};
	}

	function evictOne(){
		const oldestKey = nodeCache.keys().next().value;
		const old = nodeCache.get(oldestKey);
		stats.cacheBytes -= old.local.byteLength;
		cachedPoints -= old.local.length / 3;
		nodeCache.delete(oldestKey);
		stats.cacheEvictions++;
	}
	function cachePut(node, entry){
		nodeCache.set(node, entry);
		stats.cacheBytes += entry.local.byteLength;
		cachedPoints += entry.local.length / 3;
		while(nodeCache.size > 1 && (nodeCache.size > cacheNodeLimit || cachedPoints > cachePointLimit)) evictOne();
		stats.cachedNodes = nodeCache.size;
		stats.cachedPoints = cachedPoints;
	}
	function cacheGet(node){
		const e = nodeCache.get(node);
		if(e){ nodeCache.delete(node); nodeCache.set(node, e); } // LRU touch
		return e;
	}

	// One node's cache entry from decoded node data (position relative to node.boundingBox.min, optional rgba).
	function entryFromSource(source){
		const tBuild = perfNow();
		const flat = [];
		const cols = [];
		// pass a zero offset + the node's own bbox so we collect ALL
		// of its points, in pointcloud-LOCAL coordinates, WITH colour
		// (RGB 0-255, one triplet per point, same order) so appearance
		// travels with geometry through the same cache - spec: no
		// separate full-cloud scan for RGB.
		collectNodePoints(source, ZERO, source.boundingBox, flat, cols);
		const local = Float32Array.from(flat);
		const rgb = cols.length === flat.length ? Uint8Array.from(cols) : null;
		const entry = {local, rgb, ...(useGrid ? buildGrid(local) : {grid: null})};
		if(rgb) stats.appearanceBytes += rgb.byteLength;
		stats.buildMs += perfNow() - tBuild;
		return entry;
	}

	async function buildEntry(node){
		// (3) the viewer already holds this node's points: read them (no network, no await before use)
		if(node.loaded && node.geometry){
			return entryFromSource(node);
		}
		// (4) 2.0 loader: fetch + decode without touching viewer state (same decode as node.load())
		const loader = node.octreeGeometry && node.octreeGeometry.loader;
		if(useDecodedLoad && loader && typeof loader.loadDecoded === "function"){
			await ensureHierarchyLoaded(node);
			stats.nodesLoadedOnDemand++;
			const decoded = await loader.loadDecoded(node, {bypassHttpCache: true});
			const attributes = {position: {array: decoded.position}};
			if(decoded.rgba) attributes.rgba = {array: decoded.rgba};
			return entryFromSource({boundingBox: node.boundingBox, geometry: {attributes}});
		}
		// (5) other loaders / mocks: the old load-and-poll path
		if(!node.loaded){
			await ensurePointsLoaded(node);
			stats.nodesLoadedOnDemand++;
		}
		return entryFromSource(node);
	}

	// The single way any code in this adapter obtains a node's points (queries AND prefetch).
	function getEntry(node, isPrefetch){
		const hit = cacheGet(node);
		if(hit){
			if(!isPrefetch){
				stats.cacheHits++;
				if(hit.prefetched && !hit.used){ hit.used = true; stats.prefetchUsed++; }
			}
			return Promise.resolve(hit);
		}
		const joined = inflight.get(node);
		if(joined){
			if(!isPrefetch){
				stats.inflightJoins++;
				if(joined.prefetched && !joined.used){ joined.used = true; stats.prefetchUsed++; }
			}
			return joined.promise;
		}
		const rec = {prefetched: isPrefetch, used: !isPrefetch, promise: null};
		rec.promise = buildEntry(node).then((entry) => {
			inflight.delete(node);
			if(isPrefetch) prefetchInflight--;
			entry.prefetched = rec.prefetched;
			entry.used = rec.used;
			if(!disposed) cachePut(node, entry);
			return entry;
		}, (err) => {
			inflight.delete(node);
			if(isPrefetch) prefetchInflight--;
			throw err;
		});
		inflight.set(node, rec);
		if(isPrefetch){ prefetchInflight++; stats.prefetchStarted++; }
		else stats.cacheMisses++;
		return rec.promise;
	}

	async function queryPoints(center, tangent, radialExtent, axialHalfLength){
		const tQuery = perfNow();
		try{
			return await queryPointsImpl(center, tangent, radialExtent, axialHalfLength);
		}finally{
			stats.queryMs += perfNow() - tQuery;
		}
	}

	async function queryPointsImpl(center, tangent, radialExtent, axialHalfLength){
		stats.queries++;
		if(shouldCancel()) return [];

		const t = tangent.clone().normalize();
		const ext = orientedCylinderAabbHalfExtent(t, radialExtent, axialHalfLength);
		const worldMin = center.clone().sub(ext);
		const worldMax = center.clone().add(ext);
		const r2 = radialExtent * radialExtent;
		const out = [];

		for(const pc of clouds){
			const offset = pc.position;
			const localBox = new THREE.Box3(worldMin.clone().sub(offset), worldMax.clone().sub(offset));
			const centerLocal = center.clone().sub(offset);

			let frontier;
			const tFrontier = perfNow();
			try {
				({frontier} = await selectFrontier(pc.pcoGeometry.root, localBox, pointBudget));
			} catch(e){
				stats.loadWaitMs += perfNow() - tFrontier;
				stats.frontierWaitMs += perfNow() - tFrontier;
				// cancelled mid-flight: stop quietly. A real hierarchy load FAILURE is reported (pointLoadFailed) - it must not look
				// like an area without points to the caller.
				if(shouldCancel()) return out;
				throw markLoadFailure(e);
			}
			stats.loadWaitMs += perfNow() - tFrontier;
				stats.frontierWaitMs += perfNow() - tFrontier;
			if(shouldCancel()) return out;
			stats.nodesConsidered += frontier.length;

			// request ALL frontier nodes now (parallel), consume them below in frontier order
			const pending = frontier.map((node) => getEntry(node, false));
			// a failed fetch of a node we never get to (cancel) must not become an unhandled rejection
			for(const p of pending) p.catch(() => {});

			for(let n = 0; n < frontier.length; n++){
				const tWait = perfNow();
				let entry;
				try{
					entry = await pending[n];
				}catch(e){
					if(shouldCancel()) return out;
					throw markLoadFailure(e);
				}
				stats.loadWaitMs += perfNow() - tWait;
				if(shouldCancel()) return out;

				const tScan = perfNow();
				const L = entry.local;
				const RGB = entry.rgb;
				const testPoint = (i) => {
					const dx = L[i] - centerLocal.x, dy = L[i + 1] - centerLocal.y, dz = L[i + 2] - centerLocal.z;
					const along = dx * t.x + dy * t.y + dz * t.z;
					if(along > axialHalfLength || along < -axialHalfLength) return;
					const rx = dx - along * t.x, ry = dy - along * t.y, rz = dz - along * t.z;
					if(rx * rx + ry * ry + rz * rz > r2) return;
					const v = new THREE.Vector3(L[i] + offset.x, L[i + 1] + offset.y, L[i + 2] + offset.z);
					if(RGB) v.rgb = [RGB[i], RGB[i + 1], RGB[i + 2]];
					out.push(v);
				};
				if(entry.grid){
					// visit only the voxels the ROI's local AABB overlaps
					const {grid, vsize, gmin, gdim} = entry;
					const gx0 = Math.max(0, Math.floor((localBox.min.x - gmin[0]) / vsize));
					const gx1 = Math.min(gdim[0] - 1, Math.floor((localBox.max.x - gmin[0]) / vsize));
					const gy0 = Math.max(0, Math.floor((localBox.min.y - gmin[1]) / vsize));
					const gy1 = Math.min(gdim[1] - 1, Math.floor((localBox.max.y - gmin[1]) / vsize));
					const gz0 = Math.max(0, Math.floor((localBox.min.z - gmin[2]) / vsize));
					const gz1 = Math.min(gdim[2] - 1, Math.floor((localBox.max.z - gmin[2]) / vsize));
					for(let gx = gx0; gx <= gx1; gx++)
					for(let gy = gy0; gy <= gy1; gy++)
					for(let gz = gz0; gz <= gz1; gz++){
						const cell = grid.get((gx * gdim[1] + gy) * gdim[2] + gz);
						if(!cell) continue;
						stats.pointsScanned += cell.length;
						for(let k = 0; k < cell.length; k++) testPoint(cell[k]);
					}
				}else{
					// baseline: full linear re-scan of the whole node
					stats.pointsScanned += L.length / 3;
					for(let i = 0; i < L.length; i += 3) testPoint(i);
				}
				stats.scanMs += perfNow() - tScan;
			}
		}

		stats.pointsReturned += out.length;
		return out;
	}

	// Warms the node cache for a FUTURE ROI (same frontier selection and the same getEntry as a query). Fire-and-forget:
	// resolves when the fetches have been STARTED (not finished), never throws, never affects query results, and never
	// queues more than maxPrefetchInflight node fetches at a time (so it cannot starve the queries that actually need data).
	async function prefetch(center, tangent, radialExtent, axialHalfLength){
		try{
			if(disposed || shouldCancel() || maxPrefetchInflight <= 0) return;
			stats.prefetchRequests++;
			const t = tangent.clone().normalize();
			const ext = orientedCylinderAabbHalfExtent(t, radialExtent, axialHalfLength);
			const worldMin = center.clone().sub(ext);
			const worldMax = center.clone().add(ext);
			for(const pc of clouds){
				const offset = pc.position;
				const localBox = new THREE.Box3(worldMin.clone().sub(offset), worldMax.clone().sub(offset));
				const {frontier} = await selectFrontier(pc.pcoGeometry.root, localBox, pointBudget);
				if(disposed || shouldCancel()) return;
				for(const node of frontier){
					if(prefetchInflight >= maxPrefetchInflight) return;
					if(nodeCache.has(node) || inflight.has(node)) continue;
					getEntry(node, true).catch(() => {});
				}
			}
		}catch(e){
			// opportunistic: a failed prefetch is simply not cached; the real query will fetch (and report errors) itself
		}
	}

	queryPoints.stats = stats; // trackPipe læser timing-tællerne herfra
	queryPoints.prefetch = prefetch;
	return {
		queryPoints,
		stats,
		prefetch,
		dispose(){
			disposed = true;
			nodeCache.clear();
			inflight.clear();
			cachedPoints = 0;
			stats.cacheBytes = 0;
			stats.cachedNodes = 0;
			stats.cachedPoints = 0;
		},
	};
}

const ZERO = new THREE.Vector3(0, 0, 0);

// Exported for unit testing the ROI-AABB math in isolation.
export {orientedCylinderAabbHalfExtent};
